import { Button, Stack } from "@mui/joy";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import agentNoteCommentSchema, { AgentNoteCommentFormType } from "@/schemas/agents/agentNoteComment";
import Input from "@/components/FormComponents/Input";
import { ArchiveOutlined } from "@mui/icons-material";
import { useState } from "react";
import { toast } from "sonner";
import { updateNotes } from "@/services/api/agents/notes";
import { useQueryClient } from "@tanstack/react-query";
import { NoteObj } from "@/types/interfaces/notes";

type AddCommentFormProps = {
  note: NoteObj;
  email: string;
};

function AddCommentForm({ note, email }: AddCommentFormProps) {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AgentNoteCommentFormType>({
    resolver: zodResolver(agentNoteCommentSchema),
    defaultValues: {
      comment: "",
    },
  });

  const onSubmit = async (data: AgentNoteCommentFormType) => {
    try {
      setIsSubmitting(true);

      await updateNotes(note._id, {
        reply: data.comment,
        reviewed: true,
      });

      // invalidate agent notes query
      queryClient.invalidateQueries({ queryKey: ["agentNotes", email] });

      reset();

      toast.success("Comment added successfully");
    } catch (err: any) {
      const res = err.response?.data;

      toast.error(res?.message || "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleArchive = async () => {
    try {
      setIsArchiving(true);

      await updateNotes(note._id, {
        active: false,
      });

      // invalidate agent notes query
      queryClient.invalidateQueries({ queryKey: ["agentNotes", email] });

      toast.success("Note archived successfully");
    } catch (err: any) {
      const res = err.response?.data;

      toast.error(res?.message || "Failed to archive note");
    } finally {
      setIsArchiving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={1.5}>
        <Input
          name="comment"
          register={register}
          type="text"
          error={errors.comment?.message}
          label="Comment"
          placeholder="Enter your comment here"
        />
        <Stack direction={"row"} gap={2}>
          <Button type="submit" loading={isSubmitting} sx={{ width: "fit-content" }}>
            Mark as Resolved
          </Button>
          <Button
            type="button"
            variant="outlined"
            loading={isArchiving}
            disabled={isArchiving || isSubmitting}
            sx={{ width: "fit-content" }}
            startDecorator={<ArchiveOutlined />}
            onClick={handleArchive}
          >
            Archive
          </Button>
        </Stack>
      </Stack>
    </form>
  );
}

export default AddCommentForm;
